import { Button, Flex, Text } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { closeModal, openModal } from '@mantine/modals';
import { MODAL_SIZE_MEDIUM } from '@shared/ui/modal';
import { IconCalendar } from '@tabler/icons-react';
import { zodResolver } from 'mantine-form-zod-resolver';
import { z } from 'zod';

const MODAL_ID = 'change-candidate-readiness-date';

const $ChangeCandidateReadinessDate = z.object({
  readinessDate: z.date({
    required_error: 'Выберите дату готовности к выходу на работу',
  }),
});

type ChangeCandidateReadinessDateValues = z.infer<
  typeof $ChangeCandidateReadinessDate
>;

type ChangeCandidateReadinessDateModalProps = {
  initialDate?: Date | null;
  onSave: (date: Date) => void;
};

const ChangeCandidateReadinessDateModal = ({
  initialDate,
  onSave,
}: ChangeCandidateReadinessDateModalProps) => {
  const form = useForm<ChangeCandidateReadinessDateValues>({
    initialValues: {
      readinessDate: initialDate || new Date(),
    },
    validate: zodResolver($ChangeCandidateReadinessDate),
  });

  const handleSubmit = ({
    readinessDate,
  }: ChangeCandidateReadinessDateValues) => {
    onSave(readinessDate);
    closeModal(MODAL_ID);
  };

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Flex direction='column' gap={24} w='100%'>
        <Text c='tertiary.7' size='sm'>
          Выберите дату готовности кандидата к выходу на работу
        </Text>

        <DateInput
          key={form.key('readinessDate')}
          {...form.getInputProps('readinessDate')}
          placeholder='Выберите дату'
          w='100%'
          leftSection={<IconCalendar color='var(--mantine-color-primary-4)' />}
          clearable
          valueFormat='DD.MM.YYYY'
        />

        <Flex gap={8} justify='flex-end'>
          <Button
            type='button'
            variant='outline'
            onClick={() => closeModal(MODAL_ID)}
          >
            Отмена
          </Button>
          <Button type='submit'>Сохранить</Button>
        </Flex>
      </Flex>
    </form>
  );
};

export const useChangeCandidateReadinessDate = () => {
  const openChangeCandidateReadinessDate = ({
    initialDate,
    onSave,
  }: ChangeCandidateReadinessDateModalProps) =>
    openModal({
      modalId: MODAL_ID,
      title: 'Изменить дату готовности к выходу на работу',
      size: MODAL_SIZE_MEDIUM,
      children: (
        <ChangeCandidateReadinessDateModal
          initialDate={initialDate}
          onSave={onSave}
        />
      ),
    });

  return { openChangeCandidateReadinessDate };
};
